import { Box, BoxProps, Stack } from '@chakra-ui/react';
import {
  type StepperProps,
  Step,
  StepDescription,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
} from '@diamond/shared/ui';

export interface FormStepperStep {
  title: string;
  description?: string;
}

export interface FormStepperProps
  extends Omit<StepperProps, 'index' | 'children'> {
  steps: FormStepperStep[];
  currentStep: number;
  containerProps?: BoxProps;
  onStepClick?: (stepIndex: number) => void;
  allowClickToNavigate?: boolean;
}

export function FormStepper({
  steps,
  currentStep,
  containerProps,
  orientation = 'horizontal',
  onStepClick,
  allowClickToNavigate = false,
  ...stepperProps
}: FormStepperProps) {
  if (!steps.length) {
    return null;
  }

  const maxIndex = steps.length - 1;
  const activeStep = Math.min(Math.max(currentStep, 0), maxIndex);

  const handleStepClick = (stepIndex: number) => {
    if (allowClickToNavigate && onStepClick) {
      onStepClick(stepIndex);
    }
  };

  return (
    <Box {...containerProps}>
      <Stepper index={activeStep} orientation={orientation} {...stepperProps}>
        {steps.map((step, index) => (
          <Step key={step.title} flex={1} alignItems="center" gap={1}>
            <Stack
              spacing={{ base: 2, md: 3 }}
              alignItems="center"
              textAlign="center"
              flexShrink={0}
              minW={{ base: '80px' }}
              maxW={{ base: '150px' }}
            >
              <StepIndicator
                cursor={allowClickToNavigate ? 'pointer' : 'default'}
                onClick={() => handleStepClick(index)}
                _hover={
                  allowClickToNavigate
                    ? {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s',
                      }
                    : {}
                }
              >
                <StepStatus
                  complete={<StepNumber />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>

              <Box
                cursor={allowClickToNavigate ? 'pointer' : 'default'}
                onClick={() => handleStepClick(index)}
                _hover={
                  allowClickToNavigate
                    ? {
                        opacity: 0.8,
                        transition: 'opacity 0.2s',
                      }
                    : {}
                }
                w="full"
                maxW="inherit"
              >
                <StepTitle
                  fontSize={{ base: 'xs', md: 'sm' }}
                  lineHeight={{ base: '1.2', md: '1.3' }}
                  noOfLines={2}
                  textAlign="center"
                >
                  {step.title}
                </StepTitle>
                {step.description ? (
                  <StepDescription
                    fontSize={{ base: '2xs', md: 'xs' }}
                    noOfLines={1}
                    textAlign="center"
                  >
                    {step.description}
                  </StepDescription>
                ) : null}
              </Box>
            </Stack>

            {index !== maxIndex ? (
              <StepSeparator
                flex={1}
                minW={{ base: '20px', md: '40px' }}
                mx={{ base: 2, md: 4 }}
              />
            ) : null}
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
